'use client';

import React, { type JSX, useCallback, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { Close } from '@videoblocks/react-icons';

export interface VideoFormat {
    formatName: string;
    displayFormat: string;
    resolutionLabel: string;
    fileSize?: string;
}

interface VideoDownloadPopoverProps {
    formats: VideoFormat[];
    isDownloading: boolean;
    onSelectFormat: (formatName: string) => Promise<void>;
    onClose: () => void;
}

function stopEvent(e: React.MouseEvent): void {
    e.preventDefault();
    e.stopPropagation();
}

function FormatButton({
    format,
    isActive,
    isDownloading,
    onSelect,
    onHoverStart,
    onHoverEnd,
}: {
    format: VideoFormat;
    isActive: boolean;
    isDownloading: boolean;
    onSelect: () => void;
    onHoverStart: () => void;
    onHoverEnd: () => void;
}): JSX.Element {
    const colorClass = isActive ? 'text-gray-900' : 'text-white';
    const baseTextClass = 'text-xs font-normal';

    return (
        <button
            type="button"
            disabled={isDownloading}
            onClick={(e): void => {
                stopEvent(e);
                onSelect();
            }}
            onMouseEnter={onHoverStart}
            onMouseLeave={onHoverEnd}
            className={twMerge(
                'rounded-sm border border-white border-solid p-2 @[272px]:px-3 cursor-pointer hover:bg-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed',
                isActive && 'bg-white hover:opacity-100',
            )}
        >
            <div className="flex flex-col items-center">
                <span className={twMerge('font-bold uppercase text-xs @[272px]:text-lg @[350px]:text-xl', colorClass)}>
                    {format.resolutionLabel}
                </span>
                <div className="flex items-center gap-x-1">
                    <span className={twMerge('uppercase', baseTextClass, colorClass)}>{format.displayFormat}</span>
                    {format.fileSize && (
                        <span className={twMerge('hidden @[272px]:inline', baseTextClass, colorClass)}>
                            {format.fileSize}
                        </span>
                    )}
                </div>
            </div>
        </button>
    );
}

export function VideoDownloadPopover({
    formats,
    isDownloading,
    onSelectFormat,
    onClose,
}: VideoDownloadPopoverProps): JSX.Element {
    const [selectedFormatName, setSelectedFormatName] = useState<string | null>(null);
    const [hoveredFormatName, setHoveredFormatName] = useState<string | null>(null);
    const [isGridHovered, setIsGridHovered] = useState(false);

    const shouldShowSelectedState = !isGridHovered;

    const handleSelectFormat = useCallback(
        (formatName: string): void => {
            setSelectedFormatName(formatName);
            void onSelectFormat(formatName);
        },
        [onSelectFormat],
    );

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="download-popover-title"
            className="@container absolute inset-0 z-10 bg-gray-900/60 rounded-md h-full w-full flex justify-center items-center"
            onClick={stopEvent}
        >
            <button
                type="button"
                onClick={(e): void => {
                    stopEvent(e);
                    onClose();
                }}
                className="absolute right-2 top-2 @[350px]:right-5 @[350px]:top-5 text-white border-0 z-10 cursor-pointer"
                aria-label="Close download popover"
            >
                <Close className="w-4 h-4" fill="white" />
            </button>

            <div className="flex flex-col w-full relative items-center @[272px]:pt-0 pt-2">
                <h2 id="download-popover-title" className="text-white text-center mt-0 mb-3 text-sm @[350px]:text-base">
                    Download
                </h2>

                {formats.length === 0 && (
                    <div className="text-gray-400 text-sm text-center py-4">No formats available</div>
                )}

                {formats.length > 0 && (
                    <div
                        className="flex flex-wrap justify-center gap-1 px-2 pb-2 @[350px]:gap-2 @[350px]:pb-0"
                        onMouseEnter={(): void => setIsGridHovered(true)}
                        onMouseLeave={(): void => setIsGridHovered(false)}
                    >
                        {formats.map((fmt) => {
                            const isSelected = selectedFormatName === fmt.formatName;
                            const isHovered = hoveredFormatName === fmt.formatName;
                            const isActive = (isSelected && shouldShowSelectedState) || isHovered;

                            return (
                                <FormatButton
                                    key={fmt.formatName}
                                    format={fmt}
                                    isActive={isActive}
                                    isDownloading={isDownloading}
                                    onSelect={(): void => handleSelectFormat(fmt.formatName)}
                                    onHoverStart={(): void => setHoveredFormatName(fmt.formatName)}
                                    onHoverEnd={(): void => setHoveredFormatName(null)}
                                />
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}
